import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { markPickupScheduledFormSchema, VehicleFulfillmentStatus } from '@ekhodealer/ekho-common';
import {
	Button,
	Checkbox,
	Group,
	SkeletonRect,
	Stack,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { useGetMarkPickupScheduled } from '../../../api/progress/useGetMarkPickupScheduled';
import { useMarkPickupScheduled } from '../../../api/progress/useMarkPickupScheduled';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface MarkPickupScheduledDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	isConfirmed: boolean;
}

/**
 * MarkPickupScheduledDetail component for marking a vehicle pickup as scheduled.
 * Displays a checkbox for confirmation and a submit button.
 */
export const MarkPickupScheduledDetail = ({ actionItem }: MarkPickupScheduledDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const markPickupScheduledMutation = useMarkPickupScheduled(orderId);
	const { data: existingData, isLoading: isLoadingData } = useGetMarkPickupScheduled(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(markPickupScheduledFormSchema),
		defaultValues: {
			isConfirmed: false,
		},
	});

	// Pre-populate form with existing data
	useEffect(() => {
		if (existingData?.vehicleFulfillmentStatus === VehicleFulfillmentStatus.PICKUP_SCHEDULED) {
			reset({ isConfirmed: true });
		}
	}, [existingData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		if (!data.isConfirmed) {
			return;
		}

		try {
			await markPickupScheduledMutation.mutateAsync();
			toast({
				message: 'Pickup marked as scheduled successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to mark pickup as scheduled';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Confirm that the pickup has been scheduled with the buyer. Note: an email will be sent to the buyer communicating the updated status.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{/* Show completion status if already complete */}
				{isLoadingData ? (
					<SkeletonRect width='100%' height={48} />
				) : existingData?.vehicleFulfillmentStatus ===
				  VehicleFulfillmentStatus.PICKUP_SCHEDULED ? (
					<Text size='body' color='success'>
						✓ Pickup already scheduled
					</Text>
				) : null}

				{/* Show form if not complete */}
				{existingData?.vehicleFulfillmentStatus !==
					VehicleFulfillmentStatus.PICKUP_SCHEDULED && (
					<>
						<Controller
							name='isConfirmed'
							control={control}
							render={({ field: { onChange, value } }) => (
								<Stack itemsSpacing='4'>
									<Group
										itemsSpacing='8'
										itemsAlignY='center'
										itemsAlignX='start'>
										<Checkbox
											isSelected={value}
											onChange={onChange}
											isDisabled={
												isSubmitting ||
												markPickupScheduledMutation.isPending
											}
											ariaLabel='Confirm pickup is scheduled'
										/>
										<Text size='bodySmall'>Confirm</Text>
									</Group>
									{errors.isConfirmed && (
										<Text size='footnote' color='error'>
											{errors.isConfirmed.message}
										</Text>
									)}
								</Stack>
							)}
						/>

						<Button
							size='large'
							isFullWidth
							onPress={() => {
								submitHandler();
							}}
							isLoading={isSubmitting || markPickupScheduledMutation.isPending}
							isDisabled={isLoadingData}>
							{actionItem.config.buttonTitle || 'Mark pickup as scheduled'}
						</Button>
					</>
				)}
			</Stack>
		</Stack>
	);
};
